import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	useSortable,
	rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { httpOk, GET, POST, PUT, DELETE, FILE, resolveUploadUrl } from 'helpers/api'
import { notify } from 'helpers/helpers'
import DeleteModal from 'pages/parts/DeleteModal'
import ImageCropModal from 'pages/parts/ImageCropModal'

// Каналы — те же, что BrandBannerService::CHANNELS на бэкенде
const CHANNELS = ['website', 'mobile_app', 'telegram', 'self_order']

// Один кадр на все каналы — 2:1, 1200×600: так баннер стоит в приложении, боте и над меню
// самозаказа. Меньше 800×400 на телефоне будет мыльно, такой не принимаем
const BANNER_ASPECT = 2
const BANNER_WIDTH = 1200
const BANNER_MIN_WIDTH = 800
const UPLOAD_PATH = 'brand-banners'

// Что делает тап по баннеру. Цель хранится только у своего типа — бэкенд обнуляет остальные
const BANNER_ACTIONS = ['none', 'product', 'category', 'url']
// Как на бэкенде: ссылка уходит во внешнее приложение гостя, поэтому только эти схемы
const BANNER_URL_PATTERN = /^(https?:\/\/|tg:\/\/|tel:)\S+$/i

// Список селекта рисуется в body и сам выбирает сторону: у низа модалки он открывается
// вверх, а не уходит под кнопки и не обрезается прокруткой окна
const MODAL_SELECT_PROPS = {
	menuPosition: 'fixed',
	menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
	menuPlacement: 'auto',
	styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
}

const emptyAction = {
	type: 'none',
	product_id: null,
	category_id: null,
	url: '',
}

function SortableBannerCard({ id, children }) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id })

	return (
		<div className="col" ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.5 : 1,
			}}>
			<div className="border rounded h-100 overflow-hidden bg-white">
				<div className="d-flex align-items-center px-2 pt-2">
					<i className="uil uil-draggabledots" style={{ cursor: 'grab' }}
						{...attributes} {...listeners}></i>
				</div>
				{children}
			</div>
		</div>
	)
}

/**
 * Баннеры бренда — одна библиотека на сайт, приложение, Telegram-бота и самозаказ.
 * Баннер грузится один раз, у каждого — галочки каналов: акция «только для самозаказа»
 * — это баннер с одной галочкой. Порядок общий, действие по тапу задаётся здесь же.
 */
function BrandBanners() {
	const { t } = useTranslation()

	const [banners, setBanners] = useState([])
	const [targets, setTargets] = useState({ categories: [], products: [] })
	const [selectedItem, setSelectedItem] = useState(null)
	// Файл в окне обрезки: после «Готово» уходит на сервер и встаёт в открытую карточку
	const [cropFile, setCropFile] = useState(null)
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteId, setDeleteId] = useState(null)

	// 6px — чтобы клик по карточке не превращался в перетаскивание
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
	)

	async function getBanners() {
		const response = await GET('/brand-banners')
		if (httpOk(response)) setBanners(response.data?.data ?? [])
	}

	async function getTargets() {
		const response = await GET('/brand-banners/targets')
		if (httpOk(response)) {
			setTargets({
				categories: response.data?.categories ?? [],
				products: response.data?.products ?? [],
			})
		}
	}

	function openCreate() {
		setSelectedItem({
			'image': '',
			'image_url': '',
			'title': '',
			'channels': [...CHANNELS],
			'action': { ...emptyAction },
			'is_active': true,
		})
	}

	function openEdit(banner) {
		setSelectedItem({
			...banner,
			'channels': [...(banner.channels ?? [])],
			'action': { ...emptyAction, ...(banner.action ?? {}), url: banner.action?.url ?? '' },
		})
	}

	function toggleChannel(channel) {
		const channels = selectedItem.channels.includes(channel)
			? selectedItem.channels.filter(item => item !== channel)
			: [...selectedItem.channels, channel]

		setSelectedItem({ ...selectedItem, 'channels': channels })
	}

	function changeAction(patch) {
		setSelectedItem({ ...selectedItem, 'action': { ...selectedItem.action, ...patch } })
	}

	async function save() {
		if (!selectedItem.image) {
			notify(t('brand_banner_image_required'))
			return
		}
		if (selectedItem.channels.length === 0) {
			notify(t('brand_link_channels_required'))
			return
		}
		if (selectedItem.action.type === 'url' && !BANNER_URL_PATTERN.test((selectedItem.action.url ?? '').trim())) {
			notify(t('banner_action_url_hint'))
			return
		}

		const payload = {
			'id': selectedItem.id,
			'image': selectedItem.image,
			'title': selectedItem.title,
			'channels': selectedItem.channels,
			'action': selectedItem.action,
			'is_active': selectedItem.is_active,
		}

		const response = selectedItem.id
			? await PUT('/brand-banners', payload, { loader: true })
			: await POST('/brand-banners', payload, { loader: true })

		if (httpOk(response)) {
			setSelectedItem(null)
			getBanners()
		}
	}

	// Включить/выключить прямо из списка, не открывая карточку
	async function toggleActive(banner) {
		const response = await PUT('/brand-banners', {
			...banner,
			'is_active': !banner.is_active,
		}, { loader: true })

		if (httpOk(response)) getBanners()
	}

	async function deleteItem() {
		const response = await DELETE(`/brand-banners/${deleteId}`, {}, { loader: true })
		setModalDelete(false)
		setDeleteId(null)

		if (httpOk(response)) getBanners()
	}

	async function handleDragEnd(event) {
		const { active, over } = event
		if (!over || active.id === over.id) return

		const oldIndex = banners.findIndex(item => item.id === active.id)
		const newIndex = banners.findIndex(item => item.id === over.id)
		if (oldIndex === -1 || newIndex === -1) return

		const reordered = arrayMove(banners, oldIndex, newIndex)
		setBanners(reordered)

		const response = await PUT('/brand-banners/order', {
			'ids': reordered.map(item => item.id),
		}, { loader: false })

		// не сохранилось — возвращаем порядок с сервера, чтобы экран не расходился с гостем
		if (!httpOk(response)) getBanners()
	}

	function pickImage(e) {
		const file = e.target.files?.[0]
		e.target.value = ''
		if (!file) return

		if (!file.type?.startsWith('image/')) {
			notify(t('image2'))
			return
		}
		setCropFile(file)
	}

	async function saveCroppedImage(file) {
		setCropFile(null)

		const formData = new FormData()
		formData.append('image', file)
		formData.append('path', UPLOAD_PATH)

		const response = await FILE('/upload-image-only', formData, { loader: true })
		if (httpOk(response)) {
			setSelectedItem(prev => ({
				...prev,
				'image': response.data.url,
				'image_url': resolveUploadUrl(response.data.url, UPLOAD_PATH),
			}))
		}
	}

	function actionCaption(action) {
		if (!action || action.type === 'none') return t('banner_action_none')

		if (action.type === 'product') {
			const product = targets.products.find(item => Number(item.id) === Number(action.product_id))
			return `${t('banner_action_product')}: ${product?.name ?? `#${action.product_id}`}`
		}

		if (action.type === 'category') {
			const category = targets.categories.find(item => Number(item.id) === Number(action.category_id))
			return `${t('banner_action_category')}: ${category ? categoryLabel(category) : `#${action.category_id}`}`
		}

		return action.url
	}

	function categoryLabel(category) {
		return category.menu_name ? `${category.name} · ${category.menu_name}` : category.name
	}

	useEffect(() => {
		getBanners()
		getTargets()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const actionOptions = BANNER_ACTIONS.map(type => ({ id: type, name: t(`banner_action_${type}`) }))

	return (
		<>
			<div className="card w-100">
				<div className="card-body">
					<div className="d-flex justify-content-between mb-2">
						<div>
							<h5 className="mb-1">{t('brand_banners')}</h5>
							<small className="text-muted">{t('brand_banners_hint')}</small>
						</div>
						<div>
							<button type="button" className="btn btn-primary btn-rounded" onClick={() => openCreate()}>
								{t('add')}
							</button>
						</div>
					</div>

					{banners.length > 0 &&
						<div className="text-muted mb-2">
							<i className="uil uil-draggabledots"></i>
							<span className="ms-1">{t('brand_banners_drag_hint')}</span>
						</div>
					}

					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}>
						<SortableContext
							items={banners.map(item => item.id)}
							strategy={rectSortingStrategy}>
							{/* По три в ряд: в узкой плитке не разглядеть, влезли ли цена и товар */}
							<div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
								{banners.map(banner => (
									<SortableBannerCard key={banner.id} id={banner.id}>
										<div className="cursor-pointer mx-2 mt-2 rounded"
											onClick={() => openEdit(banner)}
											style={{
												aspectRatio: `${BANNER_ASPECT} / 1`,
												background: `url(${banner.image_url}) center / cover no-repeat #f8f9fa`,
												opacity: banner.is_active ? 1 : 0.4,
											}}>
										</div>
										<div className="p-2">
											<div className="d-flex justify-content-between align-items-center gap-2">
												<div className="td-ellipsis fw-semibold" title={banner.title ?? ''}>
													{banner.title || t('brand_banner_untitled')}
												</div>
												<div className="table-dropdown">
													<Dropdown>
														<Dropdown.Toggle variant="outline-primary">
															<i className="uil uil-ellipsis-v"></i>
														</Dropdown.Toggle>
														<Dropdown.Menu>
															<div className="table-dropdown-item" onClick={() => openEdit(banner)}>
																<i className="uil-edit-alt"></i>
																{t('edit')}
															</div>
															<div className="table-dropdown-item" onClick={() => toggleActive(banner)}>
																<i className={banner.is_active ? 'uil-eye-slash' : 'uil-eye'}></i>
																{banner.is_active ? t('brand_banner_hide') : t('brand_banner_show')}
															</div>
															<div className="table-dropdown-item text-danger"
																onClick={() => {
																	setDeleteId(banner.id)
																	setModalDelete(true)
																}}>
																<i className="uil-trash-alt"></i>
																{t('delete')}
															</div>
														</Dropdown.Menu>
													</Dropdown>
												</div>
											</div>
											<div className="d-flex flex-wrap gap-1 my-1">
												{CHANNELS.map(channel => (
													<span key={channel}
														className={`badge ${banner.channels?.includes(channel) ? 'bg-primary' : 'bg-light text-muted text-decoration-line-through'}`}>
														{t(`brand_banner_channel_${channel}`)}
													</span>
												))}
												{!banner.is_active && <span className="badge bg-danger">{t('inactive')}</span>}
											</div>
											<small className="text-muted td-ellipsis d-block" title={actionCaption(banner.action)}>
												<i className="uil-link me-1"></i>
												{actionCaption(banner.action)}
											</small>
										</div>
									</SortableBannerCard>
								))}
							</div>
						</SortableContext>
					</DndContext>

					{banners.length === 0 &&
						<div className="text-center text-muted py-4">{t('brand_banners_empty')}</div>
					}
				</div>
			</div>

			<Modal show={!!selectedItem && !cropFile} animation={false} centered scrollable
				onHide={() => setSelectedItem(null)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('brand_banner')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{selectedItem &&
						<form autoComplete="off" onSubmit={(e) => { e.preventDefault(); save() }}>
							<div className="form-group">
								<label>{t('brand_banner_image')}<span className="required-mark">*</span></label>
								<label className="d-flex align-items-center justify-content-center mb-0 cursor border rounded w-100"
									style={{
										aspectRatio: `${BANNER_ASPECT} / 1`,
										background: selectedItem.image_url ? `url(${selectedItem.image_url}) center / cover no-repeat` : '#f8f9fa',
									}}>
									{!selectedItem.image_url &&
										<div className="text-center text-muted px-3">
											<i className="fa fa-image d-block mb-2" style={{ fontSize: 28 }} />
											<span>{t('upload_photo')}</span>
										</div>
									}
									<input type="file" className="d-none" accept="image/*" onChange={e => pickImage(e)} />
								</label>
								<small className="text-muted">{t('brand_banner_image_hint')}</small>
							</div>

							<div className="form-group">
								<label>{t('brand_banner_title')}</label>
								<input type="text" className="form-control" maxLength={100}
									placeholder={t('brand_banner_title_placeholder')}
									value={selectedItem.title ?? ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'title': e.target.value })} />
							</div>

							<div className="form-group">
								<label>{t('brand_banner_channels')}</label>
								<div className="d-flex flex-wrap gap-3">
									{CHANNELS.map(channel => (
										<div key={channel} className="form-check">
											<input id={`brand-banner-channel-${channel}`} type="checkbox" className="form-check-input"
												checked={selectedItem.channels.includes(channel)}
												onChange={() => toggleChannel(channel)} />
											<label className="form-check-label" htmlFor={`brand-banner-channel-${channel}`}>
												{t(`brand_banner_channel_${channel}`)}
											</label>
										</div>
									))}
								</div>
							</div>

							<div className="form-group">
								<label>{t('banner_action')}</label>
								<Select
									{...MODAL_SELECT_PROPS}
									options={actionOptions}
									value={actionOptions.find(option => option.id === selectedItem.action.type) || actionOptions[0]}
									onChange={option => changeAction({ type: option?.id ?? 'none' })}
									getOptionLabel={option => option.name}
									getOptionValue={option => option.id}
									placeholder="" />
								{selectedItem.action.type === 'product' &&
									<Select
										{...MODAL_SELECT_PROPS}
										className="mt-2"
										options={targets.products}
										value={targets.products.find(option => Number(option.id) === Number(selectedItem.action.product_id)) || null}
										onChange={option => changeAction({ product_id: option?.id ?? null })}
										getOptionLabel={option => option.name}
										getOptionValue={option => option.id}
										noOptionsMessage={() => t('banner_action_nothing_found')}
										placeholder={t('banner_action_product_placeholder')} />
								}
								{selectedItem.action.type === 'category' &&
									<Select
										{...MODAL_SELECT_PROPS}
										className="mt-2"
										options={targets.categories}
										value={targets.categories.find(option => Number(option.id) === Number(selectedItem.action.category_id)) || null}
										onChange={option => changeAction({ category_id: option?.id ?? null })}
										getOptionLabel={option => categoryLabel(option)}
										getOptionValue={option => option.id}
										noOptionsMessage={() => t('banner_action_nothing_found')}
										placeholder={t('banner_action_category_placeholder')} />
								}
								{selectedItem.action.type === 'url' &&
									<>
										<input
											type="text"
											className={'form-control mt-2' + (selectedItem.action.url && !BANNER_URL_PATTERN.test(selectedItem.action.url.trim()) ? ' is-invalid' : '')}
											value={selectedItem.action.url ?? ''}
											onChange={e => changeAction({ url: e.target.value })}
											placeholder="https://t.me/..." />
										<small className="text-muted">{t('banner_action_url_hint')}</small>
									</>
								}
								{selectedItem.action.type === 'category' &&
									<small className="text-muted d-block">{t('brand_banner_category_hint')}</small>
								}
							</div>

							<div className="form-check form-switch">
								<input id="brand-banner-active" type="checkbox" className="form-check-input"
									checked={selectedItem.is_active}
									onChange={() => setSelectedItem({ ...selectedItem, 'is_active': !selectedItem.is_active })} />
								<label className="form-check-label" htmlFor="brand-banner-active">
									{t('brand_link_show_to_guests')}
								</label>
							</div>
						</form>
					}
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button type="button" className="btn btn-outline-warning w-100"
							onClick={() => setSelectedItem(null)}>
							{t('cancel')}
						</button>
						<button type="button" className="btn btn-primary w-100"
							onClick={() => save()}>
							{t('save')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>

			<ImageCropModal
				file={cropFile}
				aspect={BANNER_ASPECT}
				outputWidth={BANNER_WIDTH}
				minWidth={BANNER_MIN_WIDTH}
				onCancel={() => setCropFile(null)}
				onDone={saveCroppedImage} />

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}
			/>
		</>
	)
}

export default BrandBanners
